import { BUSINESS } from "@/constants/business";
import { useLang } from "@/context/LangContext";
import { STRINGS } from "@/i18n";

const MENU_PHOTOS = {
  1: {
    src: "/images/towel.jpg",
    alt: "Fresh rolled towel, candle and blooms on Mocha's massage table",
  },
  3: {
    src: "/images/shelf.jpg",
    alt: "Mocha's massage oil bottle on her professional table",
  },
  4: {
    src: "/images/table.jpg",
    alt: "Mocha's professional massage table and fresh setup",
  },
};

export default function Services() {
  const { lang } = useLang();
  const t = STRINGS[lang];
  return (
    <section id="services" data-testid="services-section" className="py-24 px-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 pb-6 border-b border-white/10 gap-6">
        <div>
          <span className="mono-lbl text-xs text-[#a4133c] tracking-[0.2em] uppercase">{t.services.label}</span>
          <h2 className="serif-disp text-4xl md:text-5xl font-light mt-2">{t.services.title}</h2>
        </div>
        <p className="text-neutral-400 text-sm max-w-md">
          {t.services.subtitle}
        </p>
      </div>

      <div data-testid="menu-list" className="border-t border-white/10">
        {t.services.menu.map((item, i) => (
          <div key={item.name}>
            <div
              data-testid={`menu-item-${i}`}
              className="border-b border-white/10 py-7 flex flex-col md:flex-row md:items-center gap-3 md:gap-6"
            >
            <div className="md:w-2/5">
              <div className="flex items-baseline gap-3 flex-wrap">
                <h3 className="serif-disp text-2xl md:text-3xl text-white font-light">{item.name}</h3>
                {item.tag && (
                  <span className="mono-lbl text-[10px] tracking-widest uppercase text-[#c9933b] border border-[#c9933b]/40 px-2 py-0.5 rounded-full">
                    {item.tag}
                  </span>
                )}
              </div>
              {item.sub && (
                <p className="mono-lbl text-[10px] uppercase tracking-widest text-[#c9933b]/80 mt-1">{item.sub}</p>
              )}
              <p className="text-neutral-400 text-sm font-light leading-relaxed mt-2 max-w-md">{item.desc}</p>
            </div>
            <div className="hidden md:block flex-1 border-b border-dotted border-white/20 -translate-y-2" />
            <div className="flex items-center gap-2 flex-wrap">
              {item.durations.map((d) => (
                <span
                  key={d.min}
                  className="mono-lbl text-xs text-[#e8c98a] border border-[#c9933b]/35 rounded-full px-4 py-1.5 whitespace-nowrap"
                >
                  {d.min} min · {d.price}
                </span>
              ))}
              <a
                data-testid={`menu-reserve-${i}`}
                href="#booking"
                className="mono-lbl text-xs uppercase tracking-widest text-[#ff758f] hover:text-white transition-colors px-2"
              >
                {t.services.reserve}
              </a>
            </div>
            </div>
            {MENU_PHOTOS[i] && (
              <div
                data-testid={`menu-photo-${i}`}
                className="relative my-5 aspect-[16/9] sm:aspect-[21/8] rounded-xl overflow-hidden border border-white/10 group"
              >
                <img
                  src={MENU_PHOTOS[i].src}
                  alt={MENU_PHOTOS[i].alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0906]/50 via-transparent to-transparent" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 text-xs text-neutral-400">
        <div data-testid="note-extra-services" className="border border-white/10 rounded-xl px-5 py-4 bg-white/[0.02]">
          <span className="mono-lbl uppercase tracking-widest text-[#c9933b] block mb-1">{t.services.notes[0].label}</span>
          {t.services.notes[0].text}
        </div>
        <div data-testid="note-outcall-policy" className="border border-white/10 rounded-xl px-5 py-4 bg-white/[0.02]">
          <span className="mono-lbl uppercase tracking-widest text-[#c9933b] block mb-1">{t.services.notes[1].label}</span>
          {t.services.notes[1].text}
        </div>
        <div data-testid="note-morning" className="border border-white/10 rounded-xl px-5 py-4 bg-white/[0.02]">
          <span className="mono-lbl uppercase tracking-widest text-[#c9933b] block mb-1">{t.services.notes[2].label}</span>
          {t.services.notes[2].text}
        </div>
      </div>

      <div
        data-testid="merch-card"
        className="mt-12 grid grid-cols-1 md:grid-cols-12 overflow-hidden rounded-2xl border border-[#c9933b]/30 bg-gradient-to-br from-[#1a1208] to-[#0b090a]"
      >
        <div className="md:col-span-5 relative aspect-[4/3] md:aspect-auto md:min-h-[280px]">
          <img
            data-testid="merch-image"
            src="/images/oils.jpg"
            alt="Mocha's shelf of hand-labeled massage and hemp body oils"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0b090a]/40 md:bg-gradient-to-r" />
        </div>
        <div className="md:col-span-7 p-8 md:p-10 flex flex-col justify-center">
          <span className="mono-lbl text-xs text-[#c9933b] tracking-[0.25em] uppercase">{t.services.merch.label}</span>
          <h3 className="serif-disp text-3xl text-white mt-2">{t.services.merch.name}</h3>
          <p className="text-neutral-400 text-sm font-light leading-relaxed mt-3 max-w-md">{t.services.merch.desc}</p>
          <p className="mono-lbl text-[11px] uppercase tracking-widest text-neutral-500 mt-3">{t.services.merch.priceNote}</p>
          <div className="mt-6">
            <a
              data-testid="merch-order-btn"
              href={BUSINESS.whatsappOilHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#1f9e54] hover:bg-[#25D366] text-white text-xs uppercase tracking-widest font-semibold rounded transition-all"
            >
              {t.services.merch.order}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
